import { useState, useRef, useEffect } from "react";
import { presignUpload, splicing } from "@/lib/api/video/hedgebeacon";
import { UploadPresignedFile } from "@/lib/file";
import { downloadFromUrl } from "@/lib/browser/download";
import { useToast } from "@/components/ui/use-toast";
import { MimeType } from "@/lib/api/utils";

export function SplicingDashboard() {
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pollingProgress, setPollingProgress] = useState<number>(0);
  const [isPolling, setIsPolling] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const checkUrlExists = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: "GET" });
      return response.ok;
    } catch {
      return false;
    }
  };

  const pollS3Url = async (url: string): Promise<boolean> => {
    const maxPollingTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    const pollInterval = 10 * 1000; // Poll every 10 seconds
    pollingStartTimeRef.current = Date.now();

    return new Promise((resolve) => {
      const poll = async () => {
        const elapsed = Date.now() - (pollingStartTimeRef.current || 0);
        const progress = Math.min((elapsed / maxPollingTime) * 100, 100);
        setPollingProgress(progress);

        if (elapsed >= maxPollingTime) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsPolling(false);
          setPollingProgress(0);
          setPreviewUrl(null);
          resolve(false);
          return;
        }

        const exists = await checkUrlExists(url);
        if (exists) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsPolling(false);
          setPollingProgress(0);
          resolve(true);
          return;
        }
      };

      // Poll immediately first time
      poll();

      // Then poll at intervals
      pollingIntervalRef.current = setInterval(poll, pollInterval);
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setError(null);
    setPreviewUrl(null);
    setIsPolling(false);
    setPollingProgress(0);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleSplice = async () => {
    if (!selectedFile) {
      setError("Please select a file first");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setPreviewUrl(null); // Clear any previous preview while polling

    try {
      // Step 1: Get presigned URL
      const presignResult = await presignUpload({
        filename: selectedFile.name,
        content_type: selectedFile.type,
      });

      if (!presignResult.success || !presignResult.data) {
        const errorMessage = presignResult.message || "Failed to get upload URL";
        setError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      const { document_id, presigned_put_url } = presignResult.data;

      // Step 2: Upload file to presigned URL
      const uploadResult = await UploadPresignedFile(
        presigned_put_url,
        selectedFile,
        selectedFile.type
      );

      if (!uploadResult.success) {
        const errorMessage = "Failed to upload file";
        setError(errorMessage);
        toast({
          title: "Upload Failed",
          description: "The file could not be uploaded",
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      // Step 3: Call splicing route
      const splicingResult = await splicing({
        document_id,
      });

      if (!splicingResult.success || !splicingResult.data) {
        const errorMessage = splicingResult.message || "Failed to splice video";
        setError(errorMessage);
        toast({
          title: "Splicing Failed",
          description: errorMessage,
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      const { presigned_get_url } = splicingResult.data;

      // Step 4: Poll S3 URL for up to 5 minutes
      setIsPolling(true);
      setPollingProgress(0);
      
      const urlExists = await pollS3Url(presigned_get_url);

      if (!urlExists) {
        const errorMessage = "Video processing timed out. The file may not be ready yet.";
        setError(errorMessage);
        toast({
          title: "Processing Timeout",
          description: errorMessage,
          variant: "destructive",
        });
        setIsProcessing(false);
        return;
      }

      // Step 5: Set preview URL
      setPreviewUrl(presigned_get_url);

      // Step 6: Download the result
      await downloadFromUrl(presigned_get_url, `spliced_${selectedFile.name}`);

      toast({
        title: "Success",
        description: "Video splicing completed and download started",
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(errorMessage);
      setPreviewUrl(null);
      setIsPolling(false);
      setPollingProgress(0);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start bg-gray-50 dark:bg-gray-900 p-4">
      <main className="w-full margin-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="flex flex-col items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Video Splicing
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 text-center mt-2">
            Upload a video file to splice with intro and outro
          </p>
        </div>

        <div className="space-y-4">
          <div
            className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={handleButtonClick}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="video/*"
              className="hidden"
              disabled={isProcessing}
            />
            <svg
              className="w-10 h-10 text-gray-400 mb-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              ></path>
            </svg>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Click to select a video file or drag and drop
            </p>
          </div>

          {selectedFile && (
            <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Selected file:
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
              </p>

              <button
                onClick={handleSplice}
                disabled={isProcessing}
                className={`mt-3 w-full py-2 px-4 rounded-md text-white font-medium text-sm
                  ${isProcessing
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {isProcessing ? 'Processing...' : 'Splice Video'}
              </button>

              {isPolling && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Waiting for video to be ready...
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {Math.round(pollingProgress)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${pollingProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    This may take up to 5 minutes...
                  </p>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-200">
                {error}
              </p>
            </div>
          )}

          {(previewUrl && !isPolling) && (
            <div className="mt-4 p-3 bg-green-100 dark:bg-green-900 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Video Preview:
                </p>
                <button
                  onClick={async () => {
                    if (selectedFile && previewUrl) {
                      try {
                        await downloadFromUrl(previewUrl, `spliced_${selectedFile.name}`);
                        toast({
                          title: "Download started",
                          description: "Your video is downloading",
                        });
                      } catch (error) {
                        const errorMessage = error instanceof Error ? error.message : "Failed to download video";
                        toast({
                          title: "Download Failed",
                          description: errorMessage,
                          variant: "destructive",
                        });
                      }
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Download Video
                </button>
              </div>
              <video
                controls
                className="w-full rounded-lg"
                src={previewUrl}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

